import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';
import { FieldShell } from '../orizon-ui/FormField';
import { useDebouncedValue } from '../orizon-ui/useDebouncedValue';
import PulsanteScanner from '../orizon-ui/PulsanteScanner';
import { useToast } from '../orizon-ui/Toast';
import { FORMATI_SCANNER } from '../../lib/gruBarcode';
import { configScorta, formattaQta } from './scorta';
import { gruLista, gruArticolo, mapGruError } from '../../lib/services/gruService';

const RigaArticolo = ({ articolo, onScegli }) => {
  const cfg = configScorta(articolo.stato_scorta);
  return (
    <button
      type="button"
      onClick={() => onScegli(articolo)}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-border/20 last:border-0"
    >
      <div className="min-w-0">
        <p className="text-[13px] font-black uppercase tracking-tight text-foreground/80 break-words">{articolo.nome}</p>
        <p className="text-[10px] font-mono font-bold text-muted-foreground mt-0.5">
          {articolo.ean}{articolo.reparto_label ? ` · ${articolo.reparto_label}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`w-2 h-2 rounded-full ${cfg.punto}`} />
        <span className={`text-[14px] tabular-nums font-black ${cfg.colore}`}>{formattaQta(articolo.qta, articolo.unita_decimali)}</span>
        <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{articolo.unita_label}</span>
      </div>
    </button>
  );
};

/**
 * Selezione di un articolo: ricerca per nome / codice / SAP (lato server, solo articoli attivi) e,
 * se è scelto un componente, scorciatoia con gli articoli compatibili con quel componente.
 * value: riga di gru_v_articoli oppure null. onChange(riga|null).
 */
const ArticoloPicker = ({
  value, onChange, componenteId, errore, obbligatorio = true, disabilitato = false, label = 'Articolo',
}) => {
  const toast = useToast();
  const [testo, setTesto] = useState('');
  const ricerca = useDebouncedValue(testo, 300);
  const [risultati, setRisultati] = useState([]);
  const [cercando, setCercando] = useState(false);
  const [compatibili, setCompatibili] = useState([]);
  const [caricando, setCaricando] = useState(false);

  // risultati della ricerca
  useEffect(() => {
    if (value || ricerca.trim().length < 2) {
      setRisultati([]);
      return undefined;
    }
    let annullato = false;
    setCercando(true);
    gruLista('articoli', { ricerca, perPagina: 8, soloAttivi: true })
      .then((r) => {
        if (annullato) return;
        setRisultati(r.righe);
        setCercando(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCercando(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricerca, value]);

  // articoli compatibili con il componente scelto
  useEffect(() => {
    if (!componenteId) {
      setCompatibili([]);
      return undefined;
    }
    let annullato = false;
    gruLista('compatibilita', { perPagina: 20, filtri: { componente_id: componenteId } })
      .then((r) => {
        if (!annullato) setCompatibili(r.righe);
      })
      .catch(() => {
        if (!annullato) setCompatibili([]);
      });
    return () => { annullato = true; };
  }, [componenteId]);

  const scegliCompatibile = async (riga) => {
    setCaricando(true);
    try {
      const a = await gruArticolo(riga.articolo_id);
      if (a) onChange(a);
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setCaricando(false);
    }
  };

  const scegli = (a) => {
    setTesto('');
    setRisultati([]);
    onChange(a);
  };

  // dopo la scansione (o il codice digitato nello scanner): cerca l'articolo con quel codice;
  // se non c'è, il codice resta nel campo e la ricerca normale prova nome / SAP / fornitore
  const dopoScansione = async (codice) => {
    setTesto(codice);
    setCaricando(true);
    try {
      const r = await gruLista('articoli', { perPagina: 1, soloAttivi: true, filtri: { ean: codice } });
      if (r.righe[0]) scegli(r.righe[0]);
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setCaricando(false);
    }
  };

  // articolo già scelto
  if (value) {
    const cfg = configScorta(value.stato_scorta);
    return (
      <FieldShell label={label} name="articolo" obbligatorio={obbligatorio} errore={errore}>
        <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-muted/20 border border-border/40">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
              <Icon name="Package" size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-black uppercase tracking-tight text-foreground break-words">{value.nome}</p>
              <p className="text-[10px] font-mono font-bold text-muted-foreground mt-0.5">
                {value.ean} · giacenza{' '}
                <span className={cfg.colore}>{formattaQta(value.qta, value.unita_decimali)} {value.unita_label}</span>
              </p>
            </div>
          </div>
          {!disabilitato && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="shrink-0 px-3 py-2 rounded-xl bg-muted/60 hover:bg-muted border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
            >
              Cambia
            </button>
          )}
        </div>
      </FieldShell>
    );
  }

  return (
    <FieldShell label={label} name="articolo-cerca" obbligatorio={obbligatorio} errore={errore}>
      <div className="flex flex-col gap-3">
        {compatibili.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-[9px] font-black uppercase tracking-[.2em] text-primary/60 px-1">Compatibili con il componente</span>
            <div className="flex flex-wrap gap-2">
              {compatibili.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  disabled={caricando || disabilitato}
                  onClick={() => scegliCompatibile(k)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 hover:bg-primary hover:text-primary-foreground border border-primary/20 text-primary text-[11px] font-black uppercase tracking-tight transition-all disabled:opacity-50"
                >
                  <Icon name="Link2" size={12} />
                  {k.articolo_nome}
                  {k.primario && <span className="text-[8px] tracking-widest opacity-70">PRIMARIO</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 items-stretch">
        <div className="relative flex-1 min-w-0">
          <Icon name="Search" size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/60" />
          <input
            id="articolo-cerca"
            type="text"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            disabled={disabilitato}
            placeholder="Cerca per nome, codice o SAP..."
            autoComplete="off"
            className={`w-full rounded-2xl pl-10 pr-4 py-3 text-sm font-bold outline-none transition-all ${
              errore
                ? 'bg-red-500/5 border-2 border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/10'
                : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
            }`}
          />
          {cercando && (
            <Icon name="Loader2" size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-primary/60" />
          )}
        </div>
        <PulsanteScanner onRilevato={dopoScansione} disabilitato={disabilitato || caricando} formati={FORMATI_SCANNER} />
        </div>

        {ricerca.trim().length >= 2 && !cercando && risultati.length === 0 && (
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 px-1">Nessun articolo trovato.</p>
        )}

        {risultati.length > 0 && (
          <div className="border border-border/40 rounded-2xl overflow-hidden bg-card">
            {risultati.map((a) => <RigaArticolo key={a.id} articolo={a} onScegli={scegli} />)}
          </div>
        )}
      </div>
    </FieldShell>
  );
};

export default ArticoloPicker;
